// src/components/shared/RecentTradesTable.jsx
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiChevronDown, HiEye, HiOutlineShare } from 'react-icons/hi';
import DashboardTradeModal from './DashboardTradeModal';
import DashboardTradeShareModal from './DashboardTradeShareModal';

const RecentTradesTable = ({ trades, loading }) => {
  const { t, i18n } = useTranslation();
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [shareTrade, setShareTrade] = useState(null);
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('latest');

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStrategyLabel = (trade) =>
    trade.customStrategyName || trade.strategyName || `${trade.symbolA} / ${trade.symbolB}`;

  const getCloseTime = (trade) => {
    const exData = trade.exchangeData || {};
    const realized = exData.realized || {};
    return realized.windowEnd || realized.fetchedAtMs || exData.lastActionAt || trade.closedAtMs || trade.updatedAt;
  };

  const strategyOptions = useMemo(() => {
    if (!trades || trades.length === 0) return [];
    const set = new Set();
    trades.forEach((trade) => set.add(getStrategyLabel(trade)));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [trades]);

  const filteredTrades = useMemo(() => {
    if (!trades || trades.length === 0) return [];

    const base = strategyFilter === 'all'
      ? trades
      : trades.filter((trade) => getStrategyLabel(trade) === strategyFilter);

    return [...base].sort((a, b) => {
      if (sortBy === 'strategy') {
        const nameA = getStrategyLabel(a);
        const nameB = getStrategyLabel(b);
        const cmp = nameA.localeCompare(nameB);
        if (cmp !== 0) return cmp;
      }

      const timeA = new Date(getCloseTime(a)).getTime() || 0;
      const timeB = new Date(getCloseTime(b)).getTime() || 0;
      return timeB - timeA;
    });
  }, [trades, strategyFilter, sortBy]);

  if (loading) return <div className="p-8 bg-slate-800 rounded-xl animate-pulse h-40"></div>;

  return (
    <div className="bg-slate-800 rounded-2xl shadow-lg border border-slate-700/50 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-slate-700/50 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <div className="text-[11px] text-slate-500 uppercase tracking-widest">
            {t('dashboard.recent_table.subtitle')}
          </div>
          <div className="text-sm text-slate-300">
            {t('dashboard.recent_table.subtitle_hint')}
          </div>
        </div>

        <div className="w-full xl:w-auto grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <span className="text-[11px] text-slate-500 uppercase tracking-widest">
              {t('dashboard.recent_table.strategy')}
            </span>
            <div className="relative mt-1">
              <select
                value={strategyFilter}
                onChange={(event) => setStrategyFilter(event.target.value)}
                className="w-full appearance-none bg-slate-900/70 border border-slate-700/60 text-slate-100 text-xs font-semibold rounded-xl pl-4 pr-9 py-2 shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="all" className="bg-slate-900 text-white">{t('dashboard.recent_table.all_strategies')}</option>
                {strategyOptions.map((name) => (
                  <option key={name} value={name} className="bg-slate-900 text-white">{name}</option>
                ))}
              </select>
              <HiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
          </div>

          <div>
            <span className="text-[11px] text-slate-500 uppercase tracking-widest">
              {t('dashboard.recent_table.sort')}
            </span>
            <div className="relative mt-1">
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value)}
                className="w-full appearance-none bg-slate-900/70 border border-slate-700/60 text-slate-100 text-xs font-semibold rounded-xl pl-4 pr-9 py-2 shadow-inner focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                <option value="latest" className="bg-slate-900 text-white">{t('dashboard.recent_table.sort_latest')}</option>
                <option value="strategy" className="bg-slate-900 text-white">{t('dashboard.recent_table.sort_strategy')}</option>
              </select>
              <HiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="px-3 sm:px-4 py-3 sm:py-4 space-y-3">
        {filteredTrades.length > 0 ? (
          filteredTrades.map((trade, idx) => {
            const exData = trade.exchangeData || {};
            const status = exData.status || 'UNKNOWN';
            const isOk = status === 'OK';
            const realizedPnl = isOk ? (exData.realized?.totalClosedPnl || 0) : 0;
            const totalFees = exData.realized?.totalFees || 0;
            const entriesCount = exData.entries?.length || 0;
            const strategyLabel = getStrategyLabel(trade);

            return (
              <div
                key={trade.publicStrategyId || idx}
                onClick={() => isOk && setSelectedTrade(trade)}
                className={`rounded-xl border border-slate-700/50 bg-slate-900/35 px-4 sm:px-5 py-4 sm:py-5 transition-colors ${
                  isOk ? 'cursor-pointer hover:bg-slate-700/35 hover:border-slate-600/60 group' : 'hover:bg-slate-700/20'
                }`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(0,1.25fr)_auto] gap-4 lg:gap-5 items-start lg:items-center">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                      <div className="text-white font-semibold truncate">{strategyLabel}</div>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-slate-700/60 text-slate-300 uppercase tracking-widest">
                        {trade.exchange}
                      </span>
                      <span className="text-[10px] px-2 py-1 rounded-full bg-blue-500/10 text-blue-300 uppercase tracking-widest">
                        {t('dashboard.recent_table.timeframe', { value: trade.timeframe ?? '-' })}
                      </span>
                      <span className={`text-[10px] px-2 py-1 rounded-full border uppercase tracking-widest ${
                        isOk ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10' : 'border-rose-500/30 text-rose-400 bg-rose-500/10'
                      }`}>
                        {status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-1 break-words">{trade.symbolA} / {trade.symbolB}</div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="min-w-0">
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                        {t('dashboard.recent_table.realized_pnl')}
                      </div>
                      <div className={`text-lg font-mono font-bold ${realizedPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {isOk ? formatCurrency(realizedPnl) : '?'}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {t('dashboard.recent_table.fees')}: ${Number(totalFees).toFixed(4)}
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                        {t('dashboard.recent_table.entries')}
                      </div>
                      <div className="text-white font-semibold">{entriesCount}</div>
                      <div className="text-[11px] text-slate-500">
                        {t('dashboard.recent_table.closed_at')}: {formatDate(getCloseTime(trade))}
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full items-center gap-2 lg:w-auto lg:justify-end">
                    {isOk && (
                      <button
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSelectedTrade(trade);
                        }}
                        title={t('dashboard.recent_table.view')}
                        className="inline-flex items-center justify-center rounded-lg border border-slate-700/70 bg-slate-900/60 px-2.5 py-2 text-slate-200 hover:bg-slate-800 hover:text-white transition-colors"
                      >
                        <HiEye className="w-4 h-4" />
                      </button>
                    )}

                    <button
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setShareTrade(trade);
                      }}
                      className="ml-auto lg:ml-0 inline-flex items-center gap-2 rounded-lg bg-blue-600/90 px-3 py-2 text-[11px] font-bold text-white shadow-lg shadow-blue-500/25 hover:bg-blue-500 transition-colors whitespace-nowrap"
                    >
                      <HiOutlineShare className="w-4 h-4" />
                      {t('dashboard.recent_table.share')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="px-6 py-10 text-center text-gray-500 italic">{t('trades.empty')}</div>
        )}
      </div>

      {selectedTrade && (
        <DashboardTradeModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
        />
      )}

      {shareTrade && (
        <DashboardTradeShareModal
          trade={shareTrade}
          onClose={() => setShareTrade(null)}
        />
      )}
    </div>
  );
};

export default RecentTradesTable;
